/**
 * Camunda 7.18 User, Group, and Tenant REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/user/
 * https://docs.camunda.org/manual/7.18/reference/rest/group/
 * https://docs.camunda.org/manual/7.18/reference/rest/tenant/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  User,
  UserProfile,
  UserCredentials,
  UserQuery,
  Group,
  GroupQuery,
  Tenant,
  TenantQuery,
  CountResult,
} from '../types';

export class UserGroupService {
  private client: CamundaApiClient;
  private userBasePath = '/engine-rest/user';
  private groupBasePath = '/engine-rest/group';
  private tenantBasePath = '/engine-rest/tenant';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Users
  // ============================================================================

  /**
   * Get users
   */
  async getUsers(query?: UserQuery): Promise<User[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<User[]>(`${this.userBasePath}${params}`);
  }

  /**
   * Get users count
   */
  async getUsersCount(query?: UserQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.userBasePath}/count${params}`);
  }

  /**
   * Get user profile by ID
   */
  async getUserProfile(id: string): Promise<UserProfile> {
    return this.client.get<UserProfile>(`${this.userBasePath}/${id}/profile`);
  }

  /**
   * Create a new user
   */
  async createUser(dto: {
    profile: {
      id: string;
      firstName?: string;
      lastName?: string;
      email?: string;
    };
    credentials: {
      password: string;
    };
  }): Promise<void> {
    await this.client.post<void>(`${this.userBasePath}/create`, dto);
  }

  /**
   * Update user profile
   */
  async updateUserProfile(
    id: string,
    profile: {
      id?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
    }
  ): Promise<void> {
    await this.client.put<void>(`${this.userBasePath}/${id}/profile`, profile);
  }

  /**
   * Update user credentials
   */
  async updateUserCredentials(
    id: string,
    credentials: UserCredentials
  ): Promise<void> {
    await this.client.put<void>(`${this.userBasePath}/${id}/credentials`, credentials);
  }

  /**
   * Delete user
   */
  async deleteUser(id: string): Promise<void> {
    await this.client.delete<void>(`${this.userBasePath}/${id}`);
  }

  /**
   * Unlock user
   */
  async unlockUser(id: string): Promise<void> {
    await this.client.post<void>(`${this.userBasePath}/${id}/unlock`, {});
  }

  // ============================================================================
  // Groups
  // ============================================================================

  /**
   * Get groups
   */
  async getGroups(query?: GroupQuery): Promise<Group[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<Group[]>(`${this.groupBasePath}${params}`);
  }

  /**
   * Get groups count
   */
  async getGroupsCount(query?: GroupQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.groupBasePath}/count${params}`);
  }

  /**
   * Get group by ID
   */
  async getGroup(id: string): Promise<Group> {
    return this.client.get<Group>(`${this.groupBasePath}/${id}`);
  }

  /**
   * Create a new group
   */
  async createGroup(group: {
    id: string;
    name?: string;
    type?: string;
  }): Promise<void> {
    await this.client.post<void>(`${this.groupBasePath}/create`, group);
  }

  /**
   * Update group
   */
  async updateGroup(
    id: string,
    group: {
      id?: string;
      name?: string;
      type?: string;
    }
  ): Promise<void> {
    await this.client.put<void>(`${this.groupBasePath}/${id}`, group);
  }

  /**
   * Delete group
   */
  async deleteGroup(id: string): Promise<void> {
    await this.client.delete<void>(`${this.groupBasePath}/${id}`);
  }

  /**
   * Get group members
   */
  async getGroupMembers(id: string): Promise<User[]> {
    return this.client.get<User[]>(`${this.groupBasePath}/${id}/members`);
  }

  /**
   * Add member to group
   */
  async addGroupMember(groupId: string, userId: string): Promise<void> {
    await this.client.put<void>(`${this.groupBasePath}/${groupId}/members/${userId}`, {});
  }

  /**
   * Remove member from group
   */
  async removeGroupMember(groupId: string, userId: string): Promise<void> {
    await this.client.delete<void>(`${this.groupBasePath}/${groupId}/members/${userId}`);
  }

  // ============================================================================
  // Tenants
  // ============================================================================

  /**
   * Get tenants
   */
  async getTenants(query?: TenantQuery): Promise<Tenant[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<Tenant[]>(`${this.tenantBasePath}${params}`);
  }

  /**
   * Get tenants count
   */
  async getTenantsCount(query?: TenantQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.tenantBasePath}/count${params}`);
  }

  /**
   * Get tenant by ID
   */
  async getTenant(id: string): Promise<Tenant> {
    return this.client.get<Tenant>(`${this.tenantBasePath}/${id}`);
  }

  /**
   * Create a new tenant
   */
  async createTenant(tenant: {
    id: string;
    name?: string;
  }): Promise<void> {
    await this.client.post<void>(`${this.tenantBasePath}/create`, tenant);
  }

  /**
   * Update tenant
   */
  async updateTenant(
    id: string,
    tenant: {
      id?: string;
      name?: string;
    }
  ): Promise<void> {
    await this.client.put<void>(`${this.tenantBasePath}/${id}`, tenant);
  }

  /**
   * Delete tenant
   */
  async deleteTenant(id: string): Promise<void> {
    await this.client.delete<void>(`${this.tenantBasePath}/${id}`);
  }

  /**
   * Add user to tenant
   */
  async addTenantUserMember(tenantId: string, userId: string): Promise<void> {
    await this.client.put<void>(`${this.tenantBasePath}/${tenantId}/user-members/${userId}`, {});
  }

  /**
   * Remove user from tenant
   */
  async removeTenantUserMember(tenantId: string, userId: string): Promise<void> {
    await this.client.delete<void>(`${this.tenantBasePath}/${tenantId}/user-members/${userId}`);
  }

  /**
   * Add group to tenant
   */
  async addTenantGroupMember(tenantId: string, groupId: string): Promise<void> {
    await this.client.put<void>(`${this.tenantBasePath}/${tenantId}/group-members/${groupId}`, {});
  }

  /**
   * Remove group from tenant
   */
  async removeTenantGroupMember(tenantId: string, groupId: string): Promise<void> {
    await this.client.delete<void>(`${this.tenantBasePath}/${tenantId}/group-members/${groupId}`);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: Record<string, any>): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let userGroupServiceInstance: UserGroupService | null = null;

export function getUserGroupService(): UserGroupService {
  if (!userGroupServiceInstance) {
    userGroupServiceInstance = new UserGroupService();
  }
  return userGroupServiceInstance;
}

export default UserGroupService;
